import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_CONFIG from '../config/api';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const legacyToken = searchParams.get('token');
  const error = searchParams.get('error');
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    let cancelled = false;

    const clearAuthParamsFromUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    };

    const finishWithToken = (token: string) => {
      localStorage.setItem('trafficLiteToken', token);
      clearAuthParamsFromUrl();
      navigate('/app');
    };

    const run = async () => {
      if (error) {
        console.error('OAuth error:', error);
        navigate('/signin?error=oauth_failed');
        return;
      }

      // Preferred: short-lived one-time code (JWT never appears in the URL)
      if (code) {
        try {
          setStatus('Completing sign-in...');
          const response = await fetch(`${API_CONFIG.API_BASE_URL}/auth/claim`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || !data?.token) {
            throw new Error(data?.error || 'Failed to claim login code');
          }
          if (!cancelled) {
            finishWithToken(data.token);
          }
          return;
        } catch (claimError) {
          console.error('❌ Login code claim failed:', claimError);
          if (!cancelled) {
            navigate('/signin?error=oauth_failed');
          }
          return;
        }
      }

      // Backward compat during deploy overlap (identity JWT only — no Google tokens)
      if (legacyToken) {
        finishWithToken(legacyToken);
        return;
      }

      console.log('❌ No login code found in AuthCallback, redirecting to signin');
      navigate('/signin');
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [code, legacyToken, error, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
